/**
 * 🔧 SmartBoutik - Enregistrement du Service Worker
 * À importer dans index.js ou App.js
 */

// Configuration
const SW_CONFIG = {
  swPath: '/service-worker.js',
  scope: '/',
  updateInterval: 60 * 60 * 1000 // Vérifier les mises à jour toutes les heures
};

// État du SW
let swRegistration = null;
let updateAvailable = false;

/**
 * Enregistrer le Service Worker
 */
export const registerServiceWorker = async () => {
  // Vérifier si les Service Workers sont supportés
  if (!('serviceWorker' in navigator)) {
    console.warn('[PWA] Service Workers non supportés');
    return null;
  }

  try {
    console.log('[PWA] Enregistrement du Service Worker...');
    
    const registration = await navigator.serviceWorker.register(
      SW_CONFIG.swPath,
      { scope: SW_CONFIG.scope }
    );
    
    swRegistration = registration;
    
    console.log('[PWA] Service Worker enregistré:', registration.scope);
    
    // Écouter les mises à jour
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // Nouvelle version disponible
            updateAvailable = true;
            console.log('[PWA] Nouvelle version disponible');
            
            // Notifier l'application
            window.dispatchEvent(new CustomEvent('sw-update-available', {
              detail: { registration }
            }));
          }
        });
      }
    });
    
    // Vérifier les mises à jour périodiquement
    setInterval(() => {
      registration.update();
    }, SW_CONFIG.updateInterval);
    
    return registration;
    
  } catch (error) {
    console.error('[PWA] Erreur enregistrement SW:', error);
    return null;
  }
};

/**
 * Désinscrire le Service Worker
 */
export const unregisterServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    
    if (registration) {
      await registration.unregister();
      console.log('[PWA] Service Worker désinscrit');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[PWA] Erreur désinscription SW:', error);
    return false;
  }
};

/**
 * Appliquer la mise à jour en attente
 */
export const applyUpdate = () => {
  if (swRegistration && swRegistration.waiting) {
    // Dire au SW en attente de prendre le contrôle
    swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // Recharger la page une fois le nouveau SW activé
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });
    
    return true;
  }
  
  return false;
};

/**
 * Vérifier si une mise à jour est disponible
 */
export const isUpdateAvailable = () => updateAvailable;

/**
 * Vider le cache du Service Worker
 */
export const clearCache = async () => {
  if (swRegistration && swRegistration.active) {
    swRegistration.active.postMessage({ type: 'CLEAR_CACHE' });
    console.log('[PWA] Cache vidé');
    return true;
  }
  
  // Fallback direct
  if ('caches' in window) {
    const names = await caches.keys();
    await Promise.all(names.map(name => caches.delete(name)));
    console.log('[PWA] Cache vidé (direct)');
    return true;
  }
  
  return false;
};

/**
 * Obtenir l'état du Service Worker
 */
export const getServiceWorkerStatus = async () => {
  if (!('serviceWorker' in navigator)) {
    return { supported: false };
  }

  const registration = await navigator.serviceWorker.getRegistration();
  
  if (!registration) {
    return { 
      supported: true, 
      registered: false 
    };
  }

  return {
    supported: true,
    registered: true,
    scope: registration.scope,
    updateAvailable: updateAvailable,
    active: !!registration.active,
    installing: !!registration.installing,
    waiting: !!registration.waiting
  };
};

/**
 * Demander la permission pour les notifications
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('[PWA] Notifications non supportées');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

/**
 * Envoyer une notification locale
 */
export const sendLocalNotification = async (title, options = {}) => {
  const hasPermission = await requestNotificationPermission();
  
  if (!hasPermission) {
    console.warn('[PWA] Permission notifications refusée');
    return false;
  }

  const defaultOptions = {
    icon: '/logo192.png',
    badge: '/logo192.png',
    vibrate: [100, 50, 100],
    ...options
  };

  if (swRegistration) {
    await swRegistration.showNotification(title, defaultOptions);
  } else {
    new Notification(title, defaultOptions);
  }

  return true;
};

/**
 * Vérifier si l'app est installée (mode standalone)
 */
export const isAppInstalled = () => {
  // iOS
  if (window.navigator.standalone === true) {
    return true;
  }
  
  // Android / Chrome
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  return false;
};

/**
 * Écouter l'événement d'installation PWA
 */
let deferredPrompt = null;

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;
  
  // Notifier l'application
  window.dispatchEvent(new CustomEvent('pwa-install-available'));
});

/**
 * Déclencher l'installation de la PWA
 */
export const promptInstall = async () => {
  if (!deferredPrompt) {
    console.warn('[PWA] Installation non disponible');
    return false;
  }

  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  
  console.log('[PWA] Résultat installation:', outcome);
  deferredPrompt = null;
  
  return outcome === 'accepted';
};

/**
 * Vérifier si l'installation est disponible
 */
export const canInstall = () => !!deferredPrompt;

/**
 * Écouter les messages du Service Worker
 */
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    console.log('[PWA] Message du SW:', event.data);
    
    if (event.data?.type === 'SYNC_REQUIRED') {
      // Notifier l'application de synchroniser
      window.dispatchEvent(new CustomEvent('sw-sync-required'));
    }
  });
}

export default {
  registerServiceWorker,
  unregisterServiceWorker,
  applyUpdate,
  isUpdateAvailable,
  clearCache,
  getServiceWorkerStatus,
  requestNotificationPermission,
  sendLocalNotification,
  isAppInstalled,
  promptInstall,
  canInstall
};
